"use client";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  CheckCircle,
  Clock,
  ExternalLink,
  Hourglass,
  Loader2,
  RefreshCw,
  RotateCcw,
  Trash2,
  XCircle,
} from "lucide-react";
import { Booking, Invitee } from "@/types/booking";
import { Separator } from "@/components/ui/separator";
import { DialogTitle } from "@/components/ui/dialog";
import { useEffect, useState } from "react";
import AddToCalendarButton from "./add-to-calendar";
import { isValidHttpUrl } from "@/helper/utility";
import { BookingInfo } from "./booking-info";
import { useLocation } from "react-router";

interface BookingSuccessProps {
  resource: any;
  booking: Booking;
  onRefresh?: () => void;
  autoRedirect?: boolean;
  isDialog?: boolean;
}

export function BookingSuccess({
  resource,
  booking,
  onRefresh,
  autoRedirect = true,
  isDialog = false,
}: BookingSuccessProps) {
  const [countdown, setCountdown] = useState(5);
  const [redirecting, setRedirecting] = useState(autoRedirect);
  const [timeLeft, setTimeLeft] = useState(30);
  const [refreshCounter, setRefreshCounter] = useState(5);
  const invitee = booking.invitees ? booking.invitees[0] : ({} as Invitee);
  const hasPaymentLink =
    invitee?.status === "waiting" &&
    invitee.payment?.link &&
    isValidHttpUrl(invitee.payment.link);

  let showWaitingMessage = false;
  if (import.meta.env.MODE !== "widget") {
    const location = useLocation();
    showWaitingMessage =
      hasPaymentLink &&
      invitee?.status === "waiting" &&
      location.pathname.includes("confirm")
        ? true
        : false;
  }

  const handleNavigate = (
    invitee: Invitee,
    action: "reschedule" | "cancel"
  ) => {
    const target = import.meta.env.MODE === "widget" ? "_blank" : "_self";
    const baseUrl = new URL(booking.booking_url).origin;
    const url = `${baseUrl}/bookings/${invitee.invitee_id}_${invitee.token}/${action}`;
    window.open(url, target);
  } ;

  useEffect(() => {
    if (!redirecting) return;
    if (hasPaymentLink) {
      const timer = setTimeout(() => {
        window.location.href = `${invitee.payment?.link}`;
      }, 2000);

      const countdownInterval = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);

      return () => {
        clearTimeout(timer);
        clearInterval(countdownInterval);
      };
    } else if (
      resource?.settings?.confirmation?.type === "redirect" &&
      resource?.settings.confirmation.redirect?.url
    ) {
      const timer = setTimeout(() => {
        window.location.href = resource.settings.confirmation.redirect.url;
      }, 5000);

      const countdownInterval = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);

      return () => {
        clearTimeout(timer);
        clearInterval(countdownInterval);
      };
    }
  }, [hasPaymentLink, invitee.payment?.link, resource, redirecting]);

  // Auto refresh if payment pending
  useEffect(() => {
    if (!showWaitingMessage) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1 || invitee?.status !== "waiting") {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });

      setRefreshCounter((prev) => {
        if (timeLeft <= 1 || invitee?.status !== "waiting") return prev;

        if (prev <= 1) {
          onRefresh?.();
          return 5;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [showWaitingMessage, invitee?.status]);

  if (hasPaymentLink && redirecting) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <div className="text-center max-w-md">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-primary" />
          <h2 className="text-2xl font-bold mb-4">Redirecting to Payment</h2>
          <p className="text-muted-foreground mb-6">
            You will be redirected to the payment page in {countdown} seconds...
          </p>
          <Button
            variant="outline"
            className="w-full"
            onClick={() => (window.location.href = `${invitee.payment?.link}`)}
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Click here if you are not redirected automatically
          </Button>
        </div>
      </div>
    );
  }

  const renderConfirmation = () => {
    const confirmation = resource.settings?.confirmation;
    if (!confirmation || autoRedirect === false) return null;

    switch (confirmation.type) {
      case "link":
        return confirmation.link.text && confirmation.link.url ? (
          <Button
            className="w-full mb-6"
            onClick={() => window.open(confirmation.link.url, "_blank")}
            variant={"outline"}
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            {confirmation.link.text}
          </Button>
        ) : null;
      case "redirect":
        return isValidHttpUrl(confirmation.redirect.url) ? (
          <div className="bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 p-4 rounded-lg mb-6">
            <p>
              Redirecting to {new URL(confirmation.redirect.url).hostname} in{" "}
              {countdown} seconds...
            </p>
            <Button
              variant="link"
              className="underline"
              onClick={() =>
                (window.location.href = `${confirmation.redirect.url}`)
              }
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Click here if you are not redirected automatically
            </Button>
          </div>
        ) : null;
      case "thanks":
        return confirmation.message?.length ? (
          <div className="bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 p-4 rounded-lg mb-6">
            <p className="whitespace-pre-wrap break-words">
              {confirmation.message}
            </p>
          </div>
        ) : null;
      default:
        return null;
    }
  };

  const confirmationContent = ({
    status,
    isDialog = false,
  }: {
    status: string;
    isDialog: boolean;
  }) => {
    const titleTag = (text: string) =>
      isDialog ? (
        <DialogTitle className="text-2xl font-bold mb-2">{text}</DialogTitle>
      ) : (
        <h2 className="text-2xl font-bold mb-2">{text}</h2>
      );

    switch (status) {
      case "confirmed":
        return {
          icon: (
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          ),
          title: titleTag("Booking Confirmed!"),
          text: "Thank you for your booking. We've sent a confirmation email with all the details.",
        };
      case "pending":
        return {
          icon: (
            <Hourglass className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          ),
          title: titleTag("Booking Submitted!"),
          text: "Thank you for your booking request. We will review it and send you a confirmation email once approved.",
        };
      case "waiting":
        return {
          icon:
            timeLeft > 0 ? (
              <Loader2 className="w-16 h-16 text-yellow-500 mx-auto mb-4 animate-spin" />
            ) : (
              <Hourglass className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            ),
          title: titleTag("Waiting for Payment"),
          text: "Please wait while we are checking your payment. We will notify you once it is confirmed",
        };
      case "canceled":
      case "cancelled":
        return {
          icon: <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />,
          title: titleTag("Booking Canceled"),
          text: "Your booking has been canceled. Please see the cancel reason, or contact the organizer.",
        };

      default:
        return {
          icon: <Clock className="w-16 h-16 text-gray-500 mx-auto mb-4" />,
          title: titleTag("Booking Status"),
          text: "Please check your booking status.",
        };
    }
  };

  const { icon, title, text } = confirmationContent({
    status: invitee.status,
    isDialog,
  });

  return (
    <div className="p-6 flex flex-col h-full">
      <div className="text-center max-w-xl mx-auto flex-1">
        {icon}
        {title}
        <p className="text-muted-foreground mb-8">{text}</p>

        {renderConfirmation()}

        {showWaitingMessage && (
          <div className="mb-8">
            {timeLeft > 0 && (
              <div className="text-xl font-bold">{timeLeft}</div>
            )}

            {timeLeft === 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onRefresh?.()}
                className="w-full"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh Now
              </Button>
            )}
          </div>
        )}

        <Card className="mb-8">
          <CardContent className="py-3">
            <BookingInfo booking={booking}></BookingInfo>
          </CardContent>
        </Card>

        {hasPaymentLink && invitee.status === "waiting" && (
          <Button className="mb-4 w-full" onClick={() => setRedirecting(true)}>
            <ExternalLink className="h-4 w-4 mr-2" />
            Retry Payment
          </Button>
        )}

        {invitee.status !== "waiting" && invitee.status !== "canceled" && (
          <AddToCalendarButton booking={booking}></AddToCalendarButton>
        )}
      </div>

      {invitee.status === "confirmed" && (
        <>
          <Separator className="my-6" />
          <div className="flex justify-center gap-4">
            {resource.settings?.policy?.reschedule?.is_enabled === true && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleNavigate(invitee, "reschedule")}
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reschedule
              </Button>
            )}
            {resource.settings?.policy?.cancellation?.is_enabled === true && (
              <Button
                variant="outline"
                size="sm"
                className="text-destructive"
                onClick={() => handleNavigate(invitee, "cancel")}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Cancel Booking
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
